/*
 * Copyright (c) 2024. Bolmate B.V.
 */
import './style/defaultEmails.scss';

import {AwesomeIcon, ChangeFunction, Checkbox, Loader} from '@dvrd/dvr-controls';
import React, {KeyboardEventHandler, MouseEventHandler} from 'react';
import DefaultEmail from "../../../models/defaultEmailModel";
import {EmailFlow} from "../../../util/interfaces";
import {BolmateButton, BolmateInput} from "../../controls";
import classNames from 'classnames';

interface Props {
    onChangeSearch: ChangeFunction<string>;
    onEnterSearch: KeyboardEventHandler;
    onChangeEmailFlow: (flow: EmailFlow) => ChangeFunction<boolean>;
    onChangeEnabled: (enabled: boolean) => ChangeFunction<boolean>;
    onChangePage: (page: number) => MouseEventHandler;
    onClickTemplate: (email: DefaultEmail) => MouseEventHandler;
    onClickNew: MouseEventHandler;
    onPreviewError: (defaultMail: DefaultEmail) => VoidFunction;
    defaultEmails: Array<DefaultEmail>;
    total: number;
    loading: boolean;
    search: string;
    emailFlows: Array<EmailFlow>;
    enabled: boolean | null;
    page: number;
    limit: number;
}

export default function DefaultEmails(props: Props) {
    const {
        onClickTemplate, onClickNew, defaultEmails, total, loading, search, emailFlows, enabled, page, limit,
        onChangeSearch, onEnterSearch, onChangeEmailFlow, onChangeEnabled, onChangePage, onPreviewError
    } = props;

    function renderFilters() {
        return (
            <div className='filters-container'>
                <BolmateInput onChange={onChangeSearch} value={search} label='Zoek een e-mail' onEnter={onEnterSearch}
                              placeholder='Naam van e-mail'/>
                <div className='checks-container'>
                    <label className='checks-label'>Flow</label>
                    <Checkbox checked={emailFlows.includes(EmailFlow.INVOICE)} onCheck={onChangeEmailFlow(EmailFlow.INVOICE)}
                              label='Factuur'/>
                    <Checkbox checked={emailFlows.includes(EmailFlow.REVIEW_INVOICE)}
                              onCheck={onChangeEmailFlow(EmailFlow.REVIEW_INVOICE)} label='Review + Factuur'/>
                    <Checkbox checked={emailFlows.includes(EmailFlow.CREDIT_INVOICE)}
                              onCheck={onChangeEmailFlow(EmailFlow.CREDIT_INVOICE)} label='Creditfactuur'/>
                </div>
                <div className='checks-container'>
                    <label className='checks-label'>Status</label>
                    <Checkbox checked={enabled === true} onCheck={onChangeEnabled(true)} label='Ingeschakeld'/>
                    <Checkbox checked={enabled === false} onCheck={onChangeEnabled(false)} label='Uitgeschakeld'/>
                </div>
                <BolmateButton onClick={onClickNew} label='+ Nieuwe template maken' secondary/>
            </div>
        )
    }

    function renderEmails() {
        const hasTemplates = defaultEmails.length > 0;
        return (
            <div className={classNames('emails-container', !hasTemplates && 'empty')}>
                <Loader active={loading}/>
                {defaultEmails.map(renderEmail)}
                {!hasTemplates && <label className='empty-label'>Geen e-mailtemplates gevonden</label>}
            </div>
        )
    }

    function renderEmail(email: DefaultEmail, index: number) {
        const statusIcon = email.enabled ? <AwesomeIcon name='check' className='enabled-icon'/> :
            <AwesomeIcon name='times' className='disabled-icon'/>;
        return (
            <div key={index} className='default-email' onClick={onClickTemplate(email)}>
                <label className='template-name'>{email.templateName}</label>
                <label className='email-status'>Status: {statusIcon} {email.enabled ? 'Ingeschakeld' :
                    'Uitgeschakeld'}</label>
                {!email.previewURLError ?
                    <div className='template-preview-container'>
                        <img src={email.previewURL ?? ''} alt='Template preview' className='template-preview'
                             onError={onPreviewError(email)}/>
                    </div> :
                    <AwesomeIcon name='image' className='preview-icon'/>}
            </div>
        )
    }

    function renderFooter() {
        const from = Math.min(total, page * limit + 1);
        const to = Math.min(total, (page + 1) * limit);
        const prevDisabled = page === 0;
        const nextDisabled = to === total;
        return (
            <div className='footer-container'>
                <AwesomeIcon name='chevron-left' className={classNames('pager', prevDisabled && 'disabled')}
                             onClick={prevDisabled ? undefined : onChangePage(page - 1)}/>
                <label className='page-label'>{from} t/m {to} van {total}</label>
                <AwesomeIcon name='chevron-right' className={classNames('pager', nextDisabled && 'disabled')}
                             onClick={nextDisabled ? undefined : onChangePage(page + 1)}/>
            </div>
        )
    }

    return (
        <div className='admin-default-emails'>
            {renderFilters()}
            {renderEmails()}
            {renderFooter()}
        </div>
    )
}